import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import BusinessIcon from '@mui/icons-material/Business'
import { Avatar, Box, Typography, Button, Alert } from '@mui/material'
import { useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { Editor, getSnapshot } from 'tldraw'

import { useOfficeForm } from '../../hooks/useOfficeForm'
import routes from '../../pages/routes'
import OfficeService from '../../services/OfficeService'
import { sessionState } from '../../state'

import OfficeDrawingEditor from './OfficeDrawingEditor'
import OfficeForm from './OfficeForm'

export default function EditOffice() {
  const [userState, setUserState] = useRecoilState(sessionState)
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // Ref to access the drawing editor
  const editorRef = useRef<Editor | null>(null)

  // Find the office to edit from the user's offices
  const officeToEdit = userState.offices?.find(office => office.oid === id)

  // State for drawing data
  const [drawingData, setDrawingData] = useState<string | null>(
    officeToEdit?.drawingData || null
  )

  const {
    formData,
    isSubmitting,
    isRetrying,
    retryCount,
    validationErrors,
    successMessage,
    updateField,
    clearMessage
  } = useOfficeForm({
    user: userState.user,
    office: officeToEdit || null,
    onUpdate: () => {
      // Navigate back to offices page after successful update
      navigate(routes.offices)
    }
  })

  const handleBack = () => {
    navigate(routes.offices)
  }

  // Handle drawing data changes
  const handleDrawingDataChange = useCallback((data: string) => {
    setDrawingData(data)
  }, [])

  // Handle clear drawing
  const handleClearDrawing = useCallback(() => {
    setDrawingData(null)
  }, [])

  // State for validation messages
  const [validationWarning, setValidationWarning] = useState<string | null>(
    null
  )
  const [validationError, setValidationError] = useState<string | null>(null)

  // Custom submit handler that includes drawing data
  const handleSubmit = useCallback(async () => {
    if (!officeToEdit?.oid || !userState.user?.uid) {
      return
    }

    // Clear previous validation messages
    setValidationWarning(null)
    setValidationError(null)

    try {
      // Capture current drawing data from the editor before submitting
      let currentDrawingData = drawingData

      // If we have an editor ref, get the latest snapshot directly
      if (editorRef.current) {
        try {
          const snapshot = getSnapshot(editorRef.current.store)
          currentDrawingData = JSON.stringify(snapshot)
        } catch (error) {
          console.error('Failed to capture drawing data from editor:', error)
        }
      }

      // Validate drawing data if present
      if (currentDrawingData) {
        const validation = OfficeService.validateDrawingData(currentDrawingData)

        if (!validation.isValid) {
          setValidationError(validation.error || 'Invalid drawing data')
          return
        }

        if (validation.exceedsWarningThreshold) {
          setValidationWarning(
            `Drawing is large (${Math.round(
              validation.sizeInBytes / 1024
            )}KB) and may impact performance. Consider simplifying your drawing.`
          )
        }
      }

      // Update office with both form data and drawing data
      const updateData = {
        ...formData,
        drawingData: currentDrawingData
      }

      await OfficeService.updateOffice(
        officeToEdit.oid,
        updateData,
        userState.user.uid
      )

      // Update session state with updated office
      const offices = userState.offices || []
      const updatedOffices = offices.map(o =>
        o.oid === officeToEdit.oid
          ? { ...o, ...updateData, updatedAt: new Date() }
          : o
      )
      setUserState(old => ({
        ...old,
        offices: updatedOffices
      }))

      // Navigate back to offices page after successful update
      navigate(routes.offices)
    } catch (error) {
      console.error('Failed to update office:', error)
      if (error instanceof Error) {
        setValidationError(
          error.message || 'Failed to save office. Please try again.'
        )
      } else {
        setValidationError('Failed to save office. Please try again.')
      }
    }
  }, [
    officeToEdit,
    userState.user,
    userState.offices,
    formData,
    drawingData,
    setUserState,
    navigate
  ])

  // Show error if office not found
  if (!officeToEdit) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Offices
        </Button>
        <Typography variant='h6' color='error'>
          Office not found or you don't have permission to edit it.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
        Back to Offices
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
          <BusinessIcon fontSize='small' />
        </Avatar>
        <Typography component='h1' variant='h6' sx={{ fontWeight: 600 }}>
          Edit Office
        </Typography>
      </Box>

      <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
        Update the details for "{officeToEdit.name}"
      </Typography>

      <OfficeForm
        formData={formData}
        isSubmitting={isSubmitting}
        isRetrying={isRetrying}
        retryCount={retryCount}
        validationErrors={validationErrors}
        successMessage={successMessage}
        onSubmit={handleSubmit}
        onChange={updateField}
        onClearMessage={clearMessage}
        isEditMode={true}
      />

      {validationWarning && (
        <Alert
          severity='warning'
          sx={{ mt: 2 }}
          onClose={() => setValidationWarning(null)}
        >
          {validationWarning}
        </Alert>
      )}

      {validationError && (
        <Alert
          severity='error'
          sx={{ mt: 2 }}
          onClose={() => setValidationError(null)}
        >
          {validationError}
        </Alert>
      )}

      <Box sx={{ mt: 3 }}>
        <OfficeDrawingEditor
          initialData={drawingData}
          onDataChange={handleDrawingDataChange}
          onClear={handleClearDrawing}
          onEditorMount={editor => {
            editorRef.current = editor
          }}
          disabled={isSubmitting}
        />
      </Box>
    </Box>
  )
}
